import { metaPixelId, trackingConsentKey } from '../data/metaPixel'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[]
  push: Fbq
  loaded: boolean
  version: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
    __jseefectPixelReady?: boolean
  }
}

type PrivacyNavigator = Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string }

/**
 * Respeta las señales de privacidad del visitante:
 * - Global Privacy Control (exigido por la ley de California)
 * - Do Not Track del navegador
 * - Una preferencia guardada como 'denied'
 */
export function isTrackingAllowed(): boolean {
  if (typeof window === 'undefined') return false
  const nav = navigator as PrivacyNavigator
  if (nav.globalPrivacyControl === true) return false
  const dnt = nav.doNotTrack ?? nav.msDoNotTrack
  if (dnt === '1' || dnt === 'yes') return false
  try {
    if (window.localStorage.getItem(trackingConsentKey) === 'denied') return false
  } catch {
    // Si el almacenamiento no está disponible, seguimos con las demás señales.
  }
  return true
}

/** Carga el código base oficial de Meta Pixel en el <head> una sola vez y registra el PageView inicial. */
export function initMetaPixel() {
  if (typeof window === 'undefined' || window.__jseefectPixelReady) return
  if (!isTrackingAllowed()) return

  // Si ya existe otra instalación del píxel, no la duplicamos.
  if (window.fbq) {
    window.__jseefectPixelReady = true
    return
  }

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue.push(args)
    }
  } as Fbq
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  window.fbq = fbq
  if (!window._fbq) window._fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  script.setAttribute('data-meta-pixel', metaPixelId)
  document.head.appendChild(script)

  window.fbq('init', metaPixelId)
  window.fbq('track', 'PageView')
  window.__jseefectPixelReady = true
}

/**
 * Registra el evento estándar Lead. Usa el ID de envío de Tally como eventID
 * para que Meta descarte duplicados. No envía datos personales.
 */
export function trackLead(submissionId: string): boolean {
  if (!window.__jseefectPixelReady || !window.fbq || !isTrackingAllowed()) return false
  window.fbq('track', 'Lead', { content_name: 'Asesoría gratuita JSEEFECT' }, { eventID: `lead_${submissionId}` })
  return true
}

/** Registra un PageView adicional (solo para cambios de página dentro del sitio). */
export function trackPageView() {
  if (window.__jseefectPixelReady && window.fbq) {
    window.fbq('track', 'PageView')
  }
}
